import { useMemo } from 'react'
import moment from 'moment'
import { CircularProgress, styled } from '@mui/material'
import { DiscussionThread } from './DiscussionThread'
import { StaticVersionSeparator } from './VersionSeparator'
import { useTargetDiscussions } from './hooks/useTargetDiscussions'
import { intl } from '../../../../../i18n'
import { TargetThread } from '../../../../../domain/entity/DiscussionEntity'
import { VersionReportVersionEntity } from '../../../../../domain/entity/version-report/VersionReportVersionEntity'
import {
  EmptyMessage,
  LoadingContainer,
  ThreadListContainer,
} from './panelStyles'

type TargetFilteredViewProps = {
  projectUuid: string
  versionPageUuid: string
  currentVersionUuid: string | undefined
  versionList: VersionReportVersionEntity[]
  targetData: ReturnType<typeof useTargetDiscussions>
}

export const TargetFilteredView = ({
  projectUuid,
  versionPageUuid,
  currentVersionUuid,
  versionList,
  targetData,
}: TargetFilteredViewProps) => {
  const { threads, isLoading, refresh } = targetData

  const versionSections = useMemo(
    () => buildVersionSections(threads, versionList),
    [threads, versionList]
  )

  if (isLoading) {
    return (
      <LoadingContainer>
        <CircularProgress />
      </LoadingContainer>
    )
  }
  if (versionSections.length === 0) {
    return (
      <EmptyMessage>
        {intl.formatMessage({ id: 'discussion.empty' })}
      </EmptyMessage>
    )
  }

  return (
    <>
      {versionSections.map(section => {
        const isCurrentVersion = section.versionUuid === currentVersionUuid
        return (
          <VersionGroup key={section.versionUuid}>
            <StaticVersionSeparator versionName={section.versionName} />
            <ThreadListContainer>
              {section.threads.map(thread => (
                <DiscussionThread
                  key={thread.threadUuid}
                  thread={thread}
                  projectUuid={projectUuid}
                  versionPageUuid={versionPageUuid}
                  onRefresh={isCurrentVersion ? refresh : () => undefined}
                  readOnly={!isCurrentVersion}
                />
              ))}
            </ThreadListContainer>
          </VersionGroup>
        )
      })}
    </>
  )
}

type VersionSection = {
  versionUuid: string
  versionName: string
  threads: TargetThread[]
}

const buildVersionSections = (
  threads: TargetThread[],
  versionList: VersionReportVersionEntity[]
): VersionSection[] => {
  const byVersion = new Map<string, TargetThread[]>()
  for (const t of threads) {
    const arr = byVersion.get(t.versionUuid) ?? []
    arr.push(t)
    byVersion.set(t.versionUuid, arr)
  }
  return [...versionList]
    .sort(
      (a, b) => moment(b.createdAt).valueOf() - moment(a.createdAt).valueOf()
    )
    .filter(v => byVersion.has(v.uuid))
    .map(v => ({
      versionUuid: v.uuid,
      versionName: v.name,
      threads: [...(byVersion.get(v.uuid) ?? [])].sort(
        (a, b) => b.createdAt - a.createdAt
      ),
    }))
}

const VersionGroup = styled('div')({
  display: 'flex',
  flexDirection: 'column',
})
